// 🧾 Documentos Emitidos (SRI) — Financiero / Gestión SRI
// Descarga los comprobantes EMITIDOS desde el portal SRI (Producción →
// Consultas → Comprobantes electrónicos emitidos). El portal consulta por día:
// la pantalla pide un rango desde–hasta y el worker lo recorre día a día.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  FileUp,
  Loader2,
  PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const FINANCIERO_HEADERS = {
  'Content-Profile': 'financiero',
  'Accept-Profile': 'financiero',
};

const TIPOS_COMPROBANTE = [
  'Factura',
  'Liquidación de compra',
  'Nota de Crédito',
  'Nota de Débito',
  'Guía de Remisión',
  'Comprobante de Retención',
];

interface JobEstado {
  id: string;
  origen: string;
  fecha_desde: string | null;
  fecha_hasta: string | null;
  tipo_comprobante: string;
  estado: 'pendiente' | 'en_proceso' | 'completado' | 'error' | 'cancelado';
  total_documentos: number | null;
  descargados: number;
  omitidos: number;
  errores: number;
  mensaje_error: string | null;
}

interface JobEvento {
  id: number;
  nivel: 'info' | 'ok' | 'warn' | 'error';
  mensaje: string;
  created_at: string;
}

const ESTADO_FINALIZADO = ['completado', 'error', 'cancelado'];

const ESTADO_BADGE: Record<JobEstado['estado'], { label: string; className: string }> = {
  pendiente: { label: 'En espera del worker', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  en_proceso: { label: 'En proceso', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  completado: { label: 'Completado', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  error: { label: 'Error', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  cancelado: { label: 'Cancelado', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

const NIVEL_CLASE: Record<JobEvento['nivel'], string> = {
  info: 'text-muted-foreground',
  ok: 'text-green-600 dark:text-green-400',
  warn: 'text-yellow-600 dark:text-yellow-400',
  error: 'text-red-600 dark:text-red-400',
};

const hoyIso = () => fechaLocal();
const haceDiasIso = (dias: number) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return fechaLocal(d);
};

export function DocumentosEmitidosSri() {
  const [fechaDesde, setFechaDesde] = useState(haceDiasIso(7));
  const [fechaHasta, setFechaHasta] = useState(hoyIso());
  const [tipo, setTipo] = useState(TIPOS_COMPROBANTE[0]);

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<JobEstado | null>(null);
  const [eventos, setEventos] = useState<JobEvento[]>([]);
  const [sinSenal, setSinSenal] = useState(false);  // worker no responde (pendiente demasiado tiempo)
  const ultimoEventoRef = useRef(0);
  const inicioRef = useRef(0);                       // ms desde que se encoló (para detectar worker caído)
  const logRef = useRef<HTMLDivElement>(null);

  const procesoActivo = !!jobId && (!job || !ESTADO_FINALIZADO.includes(job.estado));
  const SIN_SENAL_MS = 30000;

  const iniciarSeguimiento = useCallback((id: string) => {
    ultimoEventoRef.current = 0;
    inicioRef.current = Date.now();
    setSinSenal(false);
    setEventos([]);
    setJob(null);
    setJobId(id);
  }, []);

  // Retomar el proceso de emitidos activo si la pantalla se reabre
  useEffect(() => {
    apiClient
      .post('/rpc/sri_job_activo', { _origen: 'emitidos' }, { headers: FINANCIERO_HEADERS })
      .then(({ data }) => {
        if (data?.id) iniciarSeguimiento(data.id);
      })
      .catch(() => {});
  }, [iniciarSeguimiento]);

  // Polling del estado + eventos nuevos
  useEffect(() => {
    if (!jobId) return;
    if (job && ESTADO_FINALIZADO.includes(job.estado)) return;

    let detenido = false;
    const consultar = async () => {
      try {
        const { data } = await apiClient.post(
          '/rpc/sri_job_estado',
          { _job_id: jobId, _desde_evento: ultimoEventoRef.current },
          { headers: FINANCIERO_HEADERS }
        );
        if (detenido || !data?.job) return;
        setJob(data.job);
        // Worker sin señal: sigue 'pendiente' (no lo tomó nadie) tras el umbral
        setSinSenal(data.job.estado === 'pendiente' && Date.now() - inicioRef.current > SIN_SENAL_MS);
        const nuevos: JobEvento[] = data.eventos || [];
        if (nuevos.length > 0) {
          ultimoEventoRef.current = nuevos[nuevos.length - 1].id;
          setEventos((prev) => [...prev, ...nuevos]);
        }
        if (ESTADO_FINALIZADO.includes(data.job.estado)) {
          if (data.job.estado === 'completado') {
            toast.success(`Proceso completado: ${data.job.descargados} descargados, ${data.job.omitidos} omitidos`);
          } else if (data.job.estado === 'error') {
            toast.error(`El proceso terminó con error: ${data.job.mensaje_error || 'ver el log'}`);
          }
        }
      } catch {
        // error transitorio: el siguiente tick reintenta
      }
    };

    void consultar();
    const intervalo = setInterval(consultar, 1500);
    return () => {
      detenido = true;
      clearInterval(intervalo);
    };
  }, [jobId, job?.estado]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [eventos]);

  const crearJob = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(
        '/rpc/sri_crear_job_emitidos',
        { _fecha_desde: fechaDesde, _fecha_hasta: fechaHasta, _tipo_comprobante: tipo },
        { headers: FINANCIERO_HEADERS }
      );
      return data as { job_id: string };
    },
    onSuccess: (data) => {
      iniciarSeguimiento(data.job_id);
      toast.info('Proceso encolado, conectando con el SRI…');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'No se pudo iniciar el proceso');
    },
  });

  const cancelarJob = useMutation({
    mutationFn: async () => {
      await apiClient.post('/rpc/sri_cancelar_job', { _job_id: jobId }, { headers: FINANCIERO_HEADERS });
    },
    onSuccess: () => toast.warning('Cancelación solicitada'),
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'No se pudo cancelar');
    },
  });

  const badge = job ? ESTADO_BADGE[job.estado] : jobId ? ESTADO_BADGE.pendiente : null;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <FileUp className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Documentos Emitidos (SRI)</h1>
          <p className="text-sm text-muted-foreground">
            Descarga los comprobantes electrónicos emitidos (autorizados) desde el portal SRI en Línea
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Rango de fechas de emisión</CardTitle>
          <CardDescription>
            El portal consulta día por día: el proceso recorre cada fecha del rango (máx. 92 días)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label htmlFor="emi-desde" className="text-sm font-medium">Desde</label>
              <input
                id="emi-desde" type="date"
                className="block h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={fechaDesde} max={hoyIso()}
                onChange={(e) => setFechaDesde(e.target.value)}
                disabled={procesoActivo}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="emi-hasta" className="text-sm font-medium">Hasta</label>
              <input
                id="emi-hasta" type="date"
                className="block h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={fechaHasta} max={hoyIso()}
                onChange={(e) => setFechaHasta(e.target.value)}
                disabled={procesoActivo}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="emi-tipo" className="text-sm font-medium">Tipo de comprobante</label>
              <select
                id="emi-tipo"
                className="block h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                disabled={procesoActivo}
              >
                {TIPOS_COMPROBANTE.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => crearJob.mutate()}
              disabled={procesoActivo}
              isLoading={crearJob.isPending}
            >
              {!crearJob.isPending && <PlayCircle className="w-4 h-4" />}
              Procesar
            </Button>
            {procesoActivo && (
              <Button
                variant="outline"
                onClick={() => cancelarJob.mutate()}
                isLoading={cancelarJob.isPending}
              >
                {!cancelarJob.isPending && <Ban className="w-4 h-4" />}
                Cancelar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {jobId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Progreso del proceso</CardTitle>
              {badge && (
                <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${badge.className}`}>
                  {procesoActivo && <Loader2 className="w-3 h-3 animate-spin" />}
                  {job?.estado === 'completado' && <CheckCircle2 className="w-3 h-3" />}
                  {job?.estado === 'error' && <AlertCircle className="w-3 h-3" />}
                  {badge.label}
                </span>
              )}
            </div>
            {job && (
              <CardDescription>
                {job.tipo_comprobante} · {job.fecha_desde} → {job.fecha_hasta}
                {job.total_documentos !== null && ` · ${job.total_documentos} documentos`}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {sinSenal && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/15 dark:text-amber-300">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">El procesador no está respondiendo</p>
                  <p className="text-xs">El proceso lleva más de 30 s en espera y nadie lo ha tomado. El worker de descarga (sri-worker) podría estar detenido — contacta al administrador. El proceso quedará encolado y se ejecutará en cuanto el worker vuelva.</p>
                </div>
              </div>
            )}
            {job && (
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-semibold text-green-600 dark:text-green-400">{job.descargados}</p>
                  <p className="text-xs text-muted-foreground">Descargados</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-semibold">{job.omitidos}</p>
                  <p className="text-xs text-muted-foreground">Omitidos (ya existían)</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-semibold text-red-600 dark:text-red-400">{job.errores}</p>
                  <p className="text-xs text-muted-foreground">Con error</p>
                </div>
              </div>
            )}

            <div
              ref={logRef}
              className="h-72 overflow-y-auto rounded-md border bg-muted/30 p-3 font-mono text-xs space-y-1"
            >
              {eventos.length === 0 && (
                <p className="text-muted-foreground">Esperando eventos del proceso…</p>
              )}
              {eventos.map((evento) => (
                <p key={evento.id} className={NIVEL_CLASE[evento.nivel]}>
                  <span className="opacity-60">
                    {new Date(evento.created_at).toLocaleTimeString(LOCALE, { hour12: false })}
                  </span>{' '}
                  {evento.mensaje}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
